import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/shared/hooks/useAuth'
import { useProfile } from '@/shared/hooks/useProfile'
import { SwipeStack } from '@/shared/components/SwipeStack'
import type { CardProfile } from '@/shared/components/SwipeCard'

function ageFromBirthDate(iso: string | undefined): number {
  if (!iso) return 0
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 0
  const today = new Date()
  let age = today.getFullYear() - d.getFullYear()
  const m = today.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--
  return Math.max(0, age)
}

export function ProfilePreview() {
  const navigate = useNavigate()
  const userId = useAuth((s) => s.user?.id ?? null)
  const { data: me, isLoading } = useProfile(userId)

  const cardProfile: CardProfile | null = useMemo(() => {
    if (!me?.userId) return null
    const photos = (me.photos ?? [])
      .map((p: { url?: string; thumbUrl?: string | null }) => ({
        url: String(p.url ?? '').trim(),
        thumbUrl: String(p.thumbUrl ?? p.url ?? '').trim(),
      }))
      .filter((p: { url: string; thumbUrl: string }) => Boolean(p.url || p.thumbUrl))

    const interests = (me.interests ?? []).map(
      (row: {
        interestId?: number
        interest?: { id?: number; title?: string; emoji?: string | null }
      }) => ({
        id: row.interestId ?? row.interest?.id ?? 0,
        title: row.interest?.title ?? '',
        emoji: row.interest?.emoji ?? undefined,
      }),
    )

    return {
      userId: me.userId,
      name: me.name ?? '—',
      age: ageFromBirthDate(me.birthDate),
      city: me.city ?? '',
      bio: me.bio ?? undefined,
      photos,
      interests,
      height: me.heightCm ?? undefined,
      education: me.education ?? undefined,
      occupation: me.occupation ?? undefined,
      isPhoneVerified: Boolean(me.isPhoneVerified),
    }
  }, [me])

  const dismiss = async () => {
    navigate('/settings', { replace: true })
  }

  const profiles = cardProfile ? [cardProfile] : []

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900">
      <header className="flex shrink-0 items-center gap-2 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
        <button
          type="button"
          onClick={() => navigate('/settings')}
          className="rounded-full px-2 py-1 text-xl text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
          aria-label="Назад в настройки"
        >
          ←
        </button>
        <p className="min-w-0 flex-1 truncate text-xs text-gray-500 dark:text-gray-400">
          Так тебя видят в ленте · свайп — назад в настройки
        </p>
      </header>

      <div className="flex min-h-0 flex-1 flex-col items-stretch justify-center p-3 pb-6 sm:p-4">
        {isLoading || !cardProfile ? (
          <p className="px-4 text-center text-gray-500 dark:text-gray-400">
            {isLoading ? 'Загружаем анкету…' : 'Профиль не найден'}
          </p>
        ) : (
          <div className="flex h-[min(88dvh,44rem)] w-full max-w-md min-h-[22rem] flex-1 flex-col">
            <SwipeStack profiles={profiles} onLike={dismiss} onSkip={dismiss} isLoading={false} />
          </div>
        )}
      </div>
    </div>
  )
}
